import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import addSeconds from "date-fns/addSeconds";
import format from "date-fns/format";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";

// Assets
import lockIcon from "../../assets/icons/lockIcon.svg";

// Config
import { snackbarTypes } from "../../config/snackbar";
import { verifyPinUrl } from "../../config/apiConfig";
import colors from "../../config/colors";

// Hooks
import useTranslate from "../../hooks/useTranslate";

// Actions
import { parseFetchOptions } from "../../state/user/actions";
import { toggleSnack } from "../../state/snackbar/actions";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";

// Components
import Button from "../Button";
import PincodeField, { initialPincode } from "../PincodeField";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(5, 0),
    paddingLeft: 90,
    paddingRight: 90,
    [theme.breakpoints.down("xs")]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4)
    }
  },
  icon: {
    marginBottom: theme.spacing(2)
  },
  title: {
    padding: theme.spacing(1.5, 0, 1),
    color: colors.pictonBlue
  },
  label: {
    marginBottom: theme.spacing(2)
  },
  pincodeField: {
    marginBottom: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1)
  },
  submitButton: {
    width: 216,
    borderRadius: 12
  },
  buttonWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2)
  }
}));

const cooldownList = [0, 0, 0, 1, 2, 5, 10, 20, 30, 60, 120, 240, 480, 960];

const STORAGE_KEY = "unlockAt";

const loadState = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return { failCount: 0, unlockAt: null };
  try {
    const obj = JSON.parse(raw);
    return {
      failCount: Number(obj.failCount || 0),
      unlockAt: typeof obj.unlockAt === "number" ? obj.unlockAt : null
    };
  } catch {
    return { failCount: 0, unlockAt: null };
  }
};

const saveState = s => localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
const clearState = () => localStorage.removeItem(STORAGE_KEY);
const secondsUntil = unlockAt => {
  return Math.max(0, Math.floor(((unlockAt ?? 0) - Date.now()) / 1000));
};
const PincodeForm = ({ onSubmit, onError, hideHeader }) => {
  const [{ failCount, unlockAt }, setUnlock] = useState(loadState());
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pincode, setPincode] = useState(initialPincode);

  const { trackException } = useAppInsights();
  const dispatch = useDispatch();
  const classes = useStyles();
  const getText = useTranslate();
  const submitButton = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (pincode[3] !== "") submitButton.current.focus();
  }, [pincode[3]]);

  const blocked = useMemo(
    () => unlockAt != null && now < unlockAt,
    [unlockAt, now]
  );

  const handleChange = value => {
    setError(false);
    setPincode(value);
  };

  const handleFail = () => {
    const idx = Math.min(failCount + 1, cooldownList.length - 1);
    const minutes = cooldownList[idx];
    const nextUnlockAt = minutes > 0 ? Date.now() + minutes * 60 * 1000 : null;

    const newState = {
      failCount: failCount + 1,
      unlockAt: nextUnlockAt
    };
    saveState(newState);
    setUnlock(newState);
  };

  const handleSubmit = () => {
    if (blocked) return;
    setLoading(true);
    dispatch(
      parseFetchOptions(async ({ headers }) => {
        const response = await fetch(verifyPinUrl, {
          method: "PUT",
          headers: {
            ...headers,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            pin: pincode.join("")
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.pinMatches) {
            clearState();
            onSubmit();
          } else {
            setPincode(initialPincode);
            setError(true);
            setLoading(false);
            handleFail();
          }
        } else {
          trackException("Failed to check pin match");
          setLoading(false);
          dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
          onError();
        }
      })
    );
  };

  const secondsLeft = blocked ? secondsUntil(unlockAt) : 0;
  const timeFormatted = format(addSeconds(new Date(0), secondsLeft), "mm:ss");

  return (
    <form
      className={classes.contentContainer}
      onSubmit={e => e.preventDefault()}
    >
      {hideHeader ? (
        <Text
          className={classes.label}
          text="enterPasscodePage.description"
          type="contentLabel4"
        />
      ) : (
        <>
          <img className={classes.icon} src={lockIcon} alt="" />
          <Text
            type="heading1"
            className={classes.title}
            text="enterPasscodePage.description"
          />
        </>
      )}
      <PincodeField
        autoFocusOwner
        className={classes.pincodeField}
        disabled={loading}
        error={error}
        onChange={handleChange}
        value={pincode}
      />
      <div className={classes.buttonWrapper}>
        <Button
          onClick={handleSubmit}
          className={classes.submitButton}
          variant="contained"
          color="primary"
          disableElevation
          disabled={loading || pincode[3] === "" || blocked}
          ref={submitButton}
          type="submit"
        >
          {blocked
            ? getText("enterPasscodePage.retryLater", { 1: timeFormatted })
            : getText("common.prompts.continue")}
        </Button>
        {loading && <CircularProgress size={32} />}
      </div>
    </form>
  );
};

PincodeForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onError: PropTypes.func,
  hideHeader: PropTypes.bool
};

PincodeForm.defaultProps = {
  onError: () => {},
  hideHeader: false
};

export default PincodeForm;
